import React, { useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useIntercom } from 'react-use-intercom'
import { useSelector, RootStateOrAny, useDispatch } from 'react-redux'

import {
  getTransactions,
  getTransactionsPayment,
  getTransactionsPaidOut,
  getTransactionsTotalCount,
} from 'Redux/selectors/transactions'
import NotificationSystem from 'rc-notification'
import { Card, Table, CardBody, Col, Row, Container, Button } from 'reactstrap'
import CheckCircleIcon from 'mdi-react/CheckCircleIcon'

import { FullWideNotification } from 'Shared/components/Notification'
import { settings } from 'Router/routes'
import { payment, payout } from 'Constants/constants'
import Alert from 'Shared/components/Alert'
import { payoutRequestEvent, payoutRequestedEvent } from 'Helpers/tagManager'
import { User } from 'src/Types'

import { BalanceProps, Transactions } from '../BalanceProps'
import { getUserSelector, getUserId } from '../../../Redux/selectors/auth'
import InfinityScrollWrapper from '../../../Shared/components/InfinityScrollWrapper'
import { getTransactionsDetails } from '../../../Redux/actions/transactionsActions'
import {
  getUserInfo,
  logOut,
  resetUserBalance,
} from '../../../Redux/actions/auth'
import { removeCookiesHandler } from '../../../Constants/functions'

let notification: {
  notice: (arg0: {
    content: JSX.Element
    position: string
    duration: number
    closable: boolean
    style: { top: number; left: number }
    className: string
  }) => void
} | null = null

const showNotification = (errorMessage: string, topParam: number) => {
  // @ts-ignore
  notification.notice({
    content: <FullWideNotification color="danger" message={errorMessage} />,
    position: 'full',
    duration: 3,
    closable: true,
    style: { top: topParam, left: 0 },
    className: `ltr-support`,
  })
}

const Balance: React.FunctionComponent<BalanceProps> = ({
  getAllTransactions,
  orderPayout,
  recipient,
  complaints,
  hasAwaiting,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const user = useSelector(getUserSelector)
  const userId = useSelector(getUserId)
  const currentBalance: number = useSelector(
    (state: RootStateOrAny) => state.auth.user.balance
  )
  const transactions: Transactions[] = useSelector(getTransactions)

  const totalTransactions: number = useSelector(getTransactionsTotalCount)
  const transactionsPayment: number = useSelector(getTransactionsPayment)
  const transactionsPaidOut: number = useSelector(getTransactionsPaidOut)

  const [campaignRatesPage, setCampaignRatesPage] = useState(1)

  const [payoutApproved, setPayoutApproved] = useState(false)
  const [payoutDeclined, setPayoutDeclined] = useState(false)
  const [reviewComplaints, setReviewComplaints] = useState(false)
  const [requestButtonDisabled, setREquestButtonDisabled] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [isBalanceListLoading, setIsBalanceListLoading] = useState<boolean>(
    false
  )

  useEffect(() => {
    setIsBalanceListLoading(true)
    getAllTransactions(1).then(() => setIsBalanceListLoading(false))
    dispatch(getTransactionsDetails())
  }, [])

  const sendPayoutRequestedEvent = (user: User, amount: number): void => {
    payoutRequestEvent(user, amount)
  }

  const onNextPageScroll = () => {
    setIsBalanceListLoading(true)
    setCampaignRatesPage(prevValue => {
      getAllTransactions(prevValue + 1).then(() =>
        setIsBalanceListLoading(false)
      )
      return prevValue + 1
    })
  }

  const onDeclinedAlertClose = (): void => {
    setPayoutDeclined(false)
  }

  const [handlePayoutRequest] = useDebouncedCallback(() => {
    setREquestButtonDisabled(true)
    if (currentBalance < 20) {
      setErrorMessage(t('balancePage.minimumAmount'))
      setPayoutDeclined(true)
    } else if (!recipient) {
      setErrorMessage(t('balancePage.setUpBankDetails'))
      setPayoutDeclined(true)
    } else if (complaints && complaints > 0) {
      orderPayout(currentBalance, (error, response) => {
        if (!error) {
          getAllTransactions(campaignRatesPage)
          payoutRequestedEvent(currentBalance, response.id, userId)
          dispatch(resetUserBalance())
        }
      })
      setReviewComplaints(true)
      sendPayoutRequestedEvent(user, currentBalance)
    } else {
      orderPayout(currentBalance, (error, response) => {
        if (!error) {
          getAllTransactions(campaignRatesPage)
          setPayoutApproved(true)
          sendPayoutRequestedEvent(user, currentBalance)
          payoutRequestedEvent(currentBalance, response.id, userId)
          dispatch(resetUserBalance())
        } else {
          let err_text

          if (error === 'Curator have a pending payout already') {
            err_text = t('balancePage.youHavePendingPayout')
          } else {
            err_text = error
          }
          NotificationSystem.newInstance(
            { style: { top: 65 } },
            (
              // eslint-disable-next-line no-return-assign
              n: {
                notice: (arg0: {
                  content: JSX.Element
                  position: string
                  duration: number
                  closable: boolean
                  style: { top: number; left: number }
                  className: string
                }) => void
              } | null
            ) => (notification = n)
          )
          showNotification(err_text, 60)
        }
      })
    }
    setREquestButtonDisabled(false)
  }, 1000)

  const { show } = useIntercom()
  return (
    <Container className="top_row_wrapper">
      {reviewComplaints && (
        <Alert color="warning" icon>
          <div className="alert__text">
            <div className="alert__title">{t('balancePage.youHaveGot')}</div>
            <div>{t('balancePage.payoutWillReviewByModerator')}</div>
          </div>
        </Alert>
      )}
      {payoutDeclined && (
        <Alert color="danger" icon onClose={onDeclinedAlertClose}>
          <div className="alert__title">
            {t('settingsPage.payoutTransactionHasFailed')}
          </div>
          <div>
            <div>{errorMessage}</div>
          </div>
        </Alert>
      )}
      {hasAwaiting && (
        <Alert color="warning" icon>
          <div className="alert__title">
            {t('balancePage.youHavePendingPayout')}
          </div>
          <div>{t('balancePage.youWillBeAble')}</div>
        </Alert>
      )}
      {payoutApproved && (
        <Alert color="success" icon>
          <div>
            <div>{t('balancePage.ourTeamWillApprove')}</div>
          </div>
        </Alert>
      )}
      <Row>
        <Col lg={6} md={6} xl={3} xs={12}>
          <Card>
            <CardBody className="balance-card">
              <div className="dashboard__booking-total-container">
                <h5 className="dashboard__booking-total-title dashboard__booking-total-title--red">
                  ${transactionsPayment.toFixed(2)}
                </h5>
              </div>
              <h5 className="dashboard__booking-total-description">
                {t('balancePage.totalEarned')}
              </h5>
            </CardBody>
          </Card>
        </Col>
        <Col lg={6} md={6} xl={3} xs={12}>
          <Card>
            <CardBody className="balance-card">
              <div className="dashboard__booking-total-container">
                <h5 className="dashboard__booking-total-title dashboard__booking-total-title--red">
                  ${transactionsPaidOut.toFixed(2)}
                </h5>
              </div>
              <h5 className="dashboard__booking-total-description">
                {t('balancePage.paidOut')}
              </h5>
            </CardBody>
          </Card>
        </Col>
        <Col lg={6} md={6} xl={3} xs={12}>
          <Card>
            <CardBody className="balance-card">
              <div className="dashboard__booking-total-container">
                <h5 className="dashboard__booking-total-title dashboard__booking-total-title--red">
                  ${currentBalance?.toFixed(2)}
                </h5>
              </div>
              <h5 className="dashboard__booking-total-description">
                {t('balancePage.balance')}
              </h5>
            </CardBody>
          </Card>
        </Col>
        <Col lg={6} md={6} xl={3} xs={12}>
          <Card className="request-payout">
            <Button
              className="payout-btn"
              color="success"
              size="sm"
              disabled={hasAwaiting || requestButtonDisabled}
              onClick={handlePayoutRequest}
            >
              {t('balancePage.requestPayout')}
            </Button>
            <p className="subhead">{t('balancePage.minimumRequest')} $20</p>
          </Card>
        </Col>
      </Row>
      <Card>
        <CardBody>
          <InfinityScrollWrapper
            dataLength={transactions.length}
            isLoading={isBalanceListLoading}
            next={onNextPageScroll}
            hasMore={totalTransactions > transactions.length}
          >
            <Table responsive striped>
              <thead>
                <tr>
                  <th>{t('balancePage.date')}</th>
                  <th>{t('balancePage.description')}</th>
                  <th>{t('balancePage.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length ? (
                  transactions.map(
                    ({ id, amount, createdAt, type, description }) => (
                      <tr key={id}>
                        <td className="date">{moment(createdAt).fromNow()}</td>
                        <td className="description">{description}</td>
                        <td className="amount">
                          <span>
                            {type === payment || type !== payout
                              ? `$${amount.toFixed(2)}`
                              : `-$${amount.toFixed(2)}`}
                          </span>
                          <span>
                            <CheckCircleIcon />
                          </span>
                        </td>
                      </tr>
                    )
                  )
                ) : (
                  <tr>
                    {!isBalanceListLoading && (
                      <td className="empty-balance" colSpan={3}>
                        {t('balancePage.emptyBalance')}
                      </td>
                    )}
                  </tr>
                )}
              </tbody>
            </Table>
          </InfinityScrollWrapper>
        </CardBody>
      </Card>
    </Container>
  )
}

export default Balance
